import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  FaCalendarAlt,
  FaBullhorn,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaCog,
  FaDatabase,
  FaLink,
  FaSave,
  FaSpinner,
  FaSync,
  FaTimes,
  FaWhatsapp,
} from 'react-icons/fa';
import WhatsAppIntegration from './WhatsAppIntegration';

const PROVIDERS = [
  {
    id: 1,
    tipo: 'nimboxTalk',
    title: 'NimboxTalk',
    subtitle: 'Conexión con Chatwoot/NimboxTalk',
    iconUrl: 'https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/chatwoot.svg',
    tone: 'amber',
    fields: [
      { name: 'accountId', label: 'Account ID', type: 'text', placeholder: 'ID de cuenta en NimboxTalk' },
      { name: 'userToken', label: 'User Token', type: 'password', placeholder: 'Token de usuario' },
    ],
  },
  {
    id: 2,
    tipo: 'nimboxCRM',
    title: 'PerfexCRM',
    subtitle: 'Sync de leads y pipeline comercial',
    iconUrl: 'https://help.perfexcrm.com/wp-content/uploads/2017/02/favicon.jpg',
    tone: 'emerald',
    fields: [
      { name: 'url', label: 'URL API', type: 'text', placeholder: 'https://tu-perfex.com' },
      { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'authtoken de Perfex' },
      { name: 'assigned', label: 'Assigned (staff_id)', type: 'text', placeholder: 'Ej: 1' },
      { name: 'source', label: 'Source (id)', type: 'text', placeholder: 'Ej: 1' },
      { name: 'status', label: 'Status (id)', type: 'text', placeholder: 'Ej: 1' },
    ],
  },
  {
    id: 4,
    tipo: 'mautic',
    title: 'Mautic',
    subtitle: 'Marketing automation y campañas',
    iconUrl: 'https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/mautic.svg',
    tone: 'sky',
    fields: [
      { name: 'url', label: 'URL API', type: 'text', placeholder: 'https://tu-mautic.com' },
      { name: 'username', label: 'Usuario API', type: 'text', placeholder: 'usuario' },
      { name: 'password', label: 'Password API', type: 'password', placeholder: 'password o encrypted value' },
    ],
  },
  {
    id: 6,
    tipo: 'zimbraCarbonio',
    title: 'Zimbra / Carbonio',
    subtitle: 'Sync CalDAV de citas en Calendar',
    iconUrl: 'https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/zimbra.svg',
    tone: 'sky',
    fields: [
      { name: 'zimbraBaseUrl', label: 'URL interfaz Zimbra', type: 'text', placeholder: 'https://mail.tudominio.com' },
      { name: 'username', label: 'Usuario', type: 'text', placeholder: 'usuario@dominio.com' },
      { name: 'password', label: 'Password', type: 'password', placeholder: 'password de la cuenta' },
      { name: 'calendarPath', label: 'Ruta calendario (opcional)', type: 'text', placeholder: 'Calendar' },
    ],
  },
  {
    id: 5,
    tipo: 'whatsapp',
    title: 'WhatsApp',
    subtitle: 'Conexión sesiones WhatsApp + bot',
    iconUrl: 'https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/whatsapp.svg',
    tone: 'green',
    custom: 'whatsapp',
  },
  {
    id: 3,
    tipo: 'webhook',
    title: 'Webhooks',
    subtitle: 'Eventos salientes hacia tus sistemas',
    iconUrl: 'https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/webhook.svg',
    tone: 'amber',
    fields: [
      { name: 'url', label: 'URL destino', type: 'text', placeholder: 'https://tu-servidor.com/webhook' },
      { name: 'secret', label: 'Secret (opcional)', type: 'password', placeholder: 'hmac secret' },
    ],
  },
];

function toneClasses(tone) {
  if (tone === 'emerald') {
    return { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600', ring: 'ring-emerald-200' };
  }
  if (tone === 'sky') {
    return { bg: 'bg-sky-50', text: 'text-sky-700', icon: 'text-sky-600', ring: 'ring-sky-200' };
  }
  if (tone === 'green') {
    return { bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-600', ring: 'ring-green-200' };
  }
  if (tone === 'amber') {
    return { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-600', ring: 'ring-amber-200' };
  }
  return { bg: 'bg-slate-50', text: 'text-slate-700', icon: 'text-slate-600', ring: 'ring-slate-200' };
}

function Integraciones() {
  const [integraciones, setIntegraciones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [configModal, setConfigModal] = useState(null);
  const [formState, setFormState] = useState({});
  const [saving, setSaving] = useState(false);
  const [showWhatsAppConfig, setShowWhatsAppConfig] = useState(false);
  const [calendarHistorySyncLoading, setCalendarHistorySyncLoading] = useState(false);

  const authHeader = useMemo(() => {
    const token = sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');
    if (!token) {
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  const showError = (text) => setMessage({ type: 'error', text });
  const showSuccess = (text) => setMessage({ type: 'success', text });

  const fetchIntegrations = useCallback(async () => {
    if (!authHeader) {
      showError('No se encontró token de autenticación.');
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.get('/api/integraciones', { headers: authHeader });
      const fromApi = Array.isArray(response.data) ? response.data : [];
      const merged = PROVIDERS.map((base) => {
        const server = fromApi.find((item) => item.tipo === base.tipo);
        if (!server) {
          return { ...base, configuracion: {}, activo: false };
        }
        return { ...base, ...server, configuracion: server.configuracion || {} };
      });
      setIntegraciones(merged);
    } catch (error) {
      const details = error?.response?.data?.details || error?.response?.data?.error || error.message;
      showError(`No se pudo cargar integraciones: ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const activeCount = integraciones.filter((i) => i.activo).length;

  const updateIntegration = async (integration, patch) => {
    if (!authHeader) {
      showError('No se encontró token de autenticación.');
      return null;
    }

    const payload = {
      tipo: integration.tipo,
      configuracion: patch.configuracion || integration.configuracion || {},
      activo: typeof patch.activo === 'boolean' ? patch.activo : integration.activo,
    };

    const response = await axios.put(`/api/integraciones/${integration.id}`, payload, { headers: authHeader });
    return response.data;
  };

  const toggleIntegration = async (integration) => {
    try {
      const updated = await updateIntegration(integration, { activo: !integration.activo });
      if (!updated) {
        return;
      }
      setIntegraciones((prev) =>
        prev.map((item) => (item.tipo === integration.tipo ? { ...item, ...updated } : item))
      );
      showSuccess(`${integration.title}: ${updated.activo ? 'activada' : 'desactivada'}`);
    } catch (error) {
      const details = error?.response?.data?.details || error?.response?.data?.error || error.message;
      showError(`No se pudo actualizar ${integration.title}: ${details}`);
    }
  };

  const openConfig = (integration) => {
    if (integration.custom === 'whatsapp') {
      setShowWhatsAppConfig(true);
      return;
    }
    setConfigModal(integration);
    setFormState(integration.configuracion || {});
  };

  const isZimbraModal = configModal?.tipo === 'zimbraCarbonio';

  const buildGeneratedZimbraCalendarUrl = (state) => {
    const rawBase = String(state?.zimbraBaseUrl || '').trim();
    const user = String(state?.username || '').trim();
    const path = String(state?.calendarPath || 'Calendar').trim().replace(/^\/+|\/+$/g, '') || 'Calendar';
    if (!rawBase || !user) return '';

    let base = rawBase.replace(/\/+$/, '');
    try {
      const parsed = new URL(rawBase);
      base = parsed.origin;
    } catch (_error) {
      // fallback to raw input
    }

    base = base.replace(/\/dav$/i, '').replace(/\/+$/, '');
    return `${base}/dav/${encodeURIComponent(user)}/${path}`;
  };

  const getZimbraConfigForApi = () => {
    const generatedCalendarUrl = buildGeneratedZimbraCalendarUrl(formState);
    return {
      ...formState,
      calendarUrl: generatedCalendarUrl || formState.calendarUrl || '',
    };
  };

  const syncZimbraHistory = async () => {
    if (!authHeader || !isZimbraModal) {
      return;
    }
    setCalendarHistorySyncLoading(true);
    try {
      const response = await axios.post(
        '/api/integraciones/zimbra/carbonio/sync-history',
        { configuracion: getZimbraConfigForApi() },
        { headers: authHeader }
      );
      const stats = response.data?.stats || {};
      const baseMsg = `Sync histórico completado. Total: ${stats.total || 0}, upsert: ${stats.upserted || 0}, borrados: ${stats.deleted || 0}, fallos: ${stats.failed || 0}, omitidos: ${stats.skipped || 0}`;
      const sampleFailures = Array.isArray(response.data?.sample_failures) ? response.data.sample_failures : [];
      if (sampleFailures.length > 0) {
        const first = sampleFailures[0];
        const extra = first?.reason || first?.details || (first?.status ? `HTTP ${first.status}` : '');
        showError(extra ? `${baseMsg}. Primer fallo: ${extra}` : baseMsg);
      } else {
        showSuccess(baseMsg);
      }
    } catch (error) {
      const details = error?.response?.data?.details || error?.response?.data?.error || error.message;
      showError(`Error en sync histórico: ${details}`);
    } finally {
      setCalendarHistorySyncLoading(false);
    }
  };

  const saveConfiguration = async (event) => {
    event.preventDefault();
    if (!configModal) {
      return;
    }

    setSaving(true);
    try {
      const configToSave = isZimbraModal ? getZimbraConfigForApi() : formState;
      const updated = await updateIntegration(configModal, { configuracion: configToSave, activo: true });
      if (!updated) {
        return;
      }
      setIntegraciones((prev) =>
        prev.map((item) => (item.tipo === configModal.tipo ? { ...item, ...updated, activo: true } : item))
      );
      setConfigModal(null);
      showSuccess(`${configModal.title}: configuración guardada`);
    } catch (error) {
      const details = error?.response?.data?.details || error?.response?.data?.error || error.message;
      showError(`No se pudo guardar ${configModal.title}: ${details}`);
    } finally {
      setSaving(false);
    }
  };

  const syncPerfexLeads = async () => {
    if (!authHeader) {
      showError('No se encontró token de autenticación.');
      return;
    }
    setIsSyncing(true);
    try {
      const response = await axios.post('/api/sync-leads-nimboxcrm', {}, { headers: authHeader });
      if (response.data?.success) {
        const count = Array.isArray(response.data.syncedLeads) ? response.data.syncedLeads.length : 0;
        showSuccess(count > 0 ? `${count} lead(s) sincronizados con PerfexCRM` : 'Sincronización ejecutada sin nuevos leads');
      } else {
        showError(response.data?.message || 'No se pudo sincronizar leads');
      }
    } catch (error) {
      const details = error?.response?.data?.details || error?.response?.data?.error || error.message;
      showError(`Error al sincronizar leads: ${details}`);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Integraciones CRM</h1>
            <p className="mt-1 text-sm text-slate-500">
              Conecta tu stack comercial para que leads, conversaciones y agenda queden sincronizados.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-slate-100 px-4 py-2 text-sm text-slate-700">
              Activas: <span className="font-semibold">{activeCount}</span> / {integraciones.length || PROVIDERS.length}
            </div>
            <button
              onClick={fetchIntegrations}
              disabled={isLoading}
              className="inline-flex items-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? <FaSpinner className="mr-2 animate-spin" /> : <FaSync className="mr-2" />}
              Actualizar
            </button>
          </div>
        </div>
      </div>

      {message.text ? (
        <div
          className={`mb-6 rounded-xl border p-4 text-sm ${
            message.type === 'error'
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-green-200 bg-green-50 text-green-700'
          }`}
        >
          {message.text}
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex h-52 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
          <FaSpinner className="text-3xl text-slate-400 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {integraciones.map((integration) => {
            const colors = toneClasses(integration.tone);
            const Icon = integration.icon || FaCog;
            return (
              <div
                key={integration.tipo}
                className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 transition hover:shadow-md"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`rounded-xl p-3 ring-1 ${colors.bg} ${colors.ring}`}>
                      {integration.iconUrl ? (
                        <img
                          src={integration.iconUrl}
                          alt={`${integration.title} icon`}
                          className="h-5 w-5"
                        />
                      ) : (
                        <Icon className={`text-xl ${colors.icon}`} />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{integration.title}</h3>
                      <p className="text-sm text-slate-500">{integration.subtitle}</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      integration.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {integration.activo ? <FaCheckCircle className="mr-1" /> : null}
                    {integration.activo ? 'Activa' : 'Inactiva'}
                  </span>
                </div>

                <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  {integration.activo
                    ? 'La integración está habilitada para sincronización automática.'
                    : 'Activa y configura esta integración para habilitar sincronización automática.'}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => openConfig(integration)}
                    className="inline-flex items-center rounded-lg border border-blue-700 bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                    style={{ color: '#fff' }}
                  >
                    <FaCog className="mr-2" />
                    Configurar
                  </button>
                  <button
                    onClick={() => toggleIntegration(integration)}
                    className={`inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition ${
                      integration.activo
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {integration.activo ? 'Desactivar' : 'Activar'}
                  </button>
                  {integration.tipo === 'nimboxCRM' ? (
                    <button
                      onClick={syncPerfexLeads}
                      disabled={isSyncing}
                      className="inline-flex items-center rounded-lg bg-blue-100 px-3 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSyncing ? <FaSpinner className="mr-2 animate-spin" /> : <FaCloudUploadAlt className="mr-2" />}
                      Sync leads
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {configModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Configurar {configModal.title}</h2>
                <p className="text-sm text-slate-500">Guarda credenciales y activa sincronización automática.</p>
              </div>
              <button
                onClick={() => setConfigModal(null)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={saveConfiguration} className="space-y-4">
              {configModal.fields?.map((field) => (
                <div key={field.name}>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{field.label}</label>
                  <input
                    type={field.type}
                    value={formState[field.name] || ''}
                    onChange={(e) => setFormState((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none ring-0 transition focus:border-slate-500"
                  />
                </div>
              ))}

              {isZimbraModal ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-3">
                    <label className="mb-1 block text-sm font-medium text-slate-700">URL CalDAV generada</label>
                    <input
                      type="text"
                      value={buildGeneratedZimbraCalendarUrl(formState)}
                      readOnly
                      placeholder="Se genera automáticamente desde URL interfaz + usuario"
                      className="w-full rounded-xl border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-700 outline-none"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={syncZimbraHistory}
                      disabled={calendarHistorySyncLoading}
                      className="inline-flex items-center rounded-lg bg-violet-100 px-3 py-2 text-sm font-medium text-violet-700 transition hover:bg-violet-200 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {calendarHistorySyncLoading ? <FaSpinner className="mr-2 animate-spin" /> : null}
                      Sincronizar histórico
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setConfigModal(null)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ backgroundColor: '#0f172a', color: '#ffffff', border: '1px solid #0f172a' }}
                >
                  {saving ? <FaSpinner className="mr-2 animate-spin" /> : null}
                  {!saving ? <FaSave className="mr-2" /> : null}
                  Guardar y activar
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showWhatsAppConfig ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
          <div className="h-full max-h-[95vh] w-full max-w-6xl overflow-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Configuración WhatsApp</h2>
              <button
                onClick={() => setShowWhatsAppConfig(false)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                <FaTimes />
              </button>
            </div>
            <WhatsAppIntegration />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Integraciones;
